import React, { useState, useContext, useEffect } from 'react';
import { Sidebar, Menu, Icon, Button, Dropdown } from 'semantic-ui-react';

import * as Authentication from '../../../services/Authentication';
import menus from './menu';

import './style.less';
import UserContext from '../../../contexts/UserContext';
import RedirectContext from '../../../contexts/RedirectContext';
import { listInvites } from '../../../services/Invite';

interface InviteInterface {
    _id: string;
    registrantId: string;
    registrantName: string;
    registrantImage?: string;
}

const Header = ({ location, showSidebarProp }) => {
    const [showSidebar, setShowSidebar] = useState<boolean>(showSidebarProp);
    const [pageName, setPageName] = useState('Speak2 Family');
    const [isManagerRequired, setIsManagerRequired] = useState(false);
    const [showResidentScroll, setShowResidentScroll] = useState(true);

    const { userObject, setUser } = useContext(UserContext);
    const toggleRedirect = useContext(RedirectContext);
    const [invites, setInvites] = useState<InviteInterface[]>([]);
    const [isSendMessagesPage, setIsSendMessagesPage] = useState(false);
    const [isMultiSelectEnabled, setIsMultiSelectEnabled] = useState(false);
    const [isMultiMode, setIsMultiMode] = useState(false);
    const [multiModeSelectedResidentIndices, setMultiModeSelectedResidentIndices] = useState<number[]>([]);
    const navigate = (to: string) => {
        setShowSidebar(false);
        toggleRedirect(to);
        return;
    };

    const handleLogout = async () => {
        try {
            await Authentication.cognitoSignOut();

            const signedOutUser = {
                authenticatedUser: undefined,
                profile: undefined,
                authenticated: false,
            };
            setUser(signedOutUser);
            toggleRedirect('/login');
        } catch (error) {
            console.log(error);
        }
    };

    const handleMultiSelectButtonClick = async () => {
        if (isMultiMode) {
            multiModeSelectedResidentIndices.length &&
                setUser({
                    ...userObject,
                    registrants: {
                        ...userObject.registrants,
                        activeRegistrantsIndexes: multiModeSelectedResidentIndices,
                    },
                });
            setMultiModeSelectedResidentIndices([]);
            setIsMultiMode(false);
        } else {
            setIsMultiMode(true);
            setMultiModeSelectedResidentIndices([]);
        }
    };

    const getCurrentPageSettings = () => {
        const path = location.pathname;

        const [page] = menus.filter((e) => {
            const pathRegexp = new RegExp(e.regex || e.location);

            return pathRegexp.test(path);
        });

        return page || {};
    };

    useEffect(() => {
        loadInvites();
    }, []);

    useEffect(() => {
        setShowSidebar(showSidebarProp);
    }, [showSidebarProp]);

    const loadInvites = async () => {
        try {
            if (userObject.profile) {
                // @ts-ignore
                const fetchedInvites = await listInvites(userObject.profile._id);
                setInvites(fetchedInvites);
            }
        } catch (error) {
            console.log('Something went wrong');
        }
    };

    useEffect(() => {
        const { pageName, showResidentScroll, showToManagerOnly } = getCurrentPageSettings();
        setPageName(pageName);
        setIsManagerRequired(showToManagerOnly || false);
        setShowResidentScroll(showResidentScroll);
    }, [location.pathname]);

    useEffect(() => {
        const activityDetailPageRegex = /^\/activities\/?.*[a-z0-9A-Z]$/g;
        const requestDetailPageRegex = /^\/requests\/?.*[a-z0-9A-Z]$/g;
        const { pathname } = location;
        if (
            pathname === '/news' ||
            pathname === '/activities' ||
            pathname === '/archives' ||
            activityDetailPageRegex.test(pathname) ||
            requestDetailPageRegex.test(pathname)
        ) {
            setIsSendMessagesPage(true);
            setIsMultiSelectEnabled(true);
        } else {
            setIsSendMessagesPage(false);
            setIsMultiSelectEnabled(false);
            (userObject.registrants.activeRegistrantsIndexes[0] === 'all-users' ||
                userObject.registrants.activeRegistrantsIndexes.length > 1) &&
                setUser({
                    ...userObject,
                    registrants: { ...userObject.registrants, activeRegistrantsIndexes: [0] },
                });
        }
    }, [location.pathname]);

    const ProfileHeader = ({ userObject, openSideBar, pageName }) => {
        return (
            <div className="headerToolbar">
                <Button icon="bars" circular onClick={() => openSideBar()} />
                <span className="page-name">{pageName}</span>
                <div className="right">
                    <span className={`username ${invites.length && 'has-invites'}`}>
                        <Dropdown text={`${userObject.profile.FirstName}`}>
                            <Dropdown.Menu className="left">
                                <Dropdown.Item onClick={() => navigate('/profile-settings')} text="Profile Settings" />
                                <Dropdown.Item onClick={() => navigate('/invites')}>
                                    Invites
                                    <span className={`${!!invites.length ? 'invite-count' : 'no-display'}`}>
                                        {invites.length}
                                    </span>
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={handleLogout} text="Log Out" />
                            </Dropdown.Menu>
                        </Dropdown>
                    </span>
                </div>
            </div>
        );
    };

    const ConnectedRegistrantsList = ({ userObject, isManagerRequired }) => {
        if (!userObject.registrants || !userObject.registrants.list.length) return null;

        return userObject.registrants.list.map((registrant: any, index) => {
            if ((!registrant.manager && isManagerRequired) || !showResidentScroll) {
                return (
                    <div className="name-image-container">
                        <div className={`resident-image-container`} key={index}>
                            {registrant.Image ? (
                                <div
                                    className="resident-image resident-image-bk"
                                    style={{
                                        background: `url(${registrant.Image}) no-repeat`,
                                        backgroundPosition: 'center',
                                    }}
                                />
                            ) : (
                                <div className="relative-avatar">
                                    {registrant && !!registrant.FirstName && registrant.FirstName.charAt(0)}
                                    {registrant && !!registrant.LastName && registrant.LastName.charAt(0)}
                                </div>
                            )}
                            <div className="image-dimmer"></div>
                        </div>
                        <div data-tooltip={`${registrant.FirstName} ${registrant.LastName}`} data-position="top left">
                            <div className="registrant-name-header">
                                {registrant && `${registrant.FirstName} ${registrant.LastName}`}
                            </div>
                        </div>
                    </div>
                );
            }
            return (
                <div
                    key={registrant._id}
                    className={`name-image-container ${
                        pageName === 'Activities' && !registrant.canRecieveRequests && !registrant.manager
                            ? 'disabled'
                            : registrant.isDisabledForPayment ? 'disabled-for-paid': ''
                    }`}
                >
                    <div
                        className={`resident-image-container
						${
                            !isMultiMode
                                ? index ===
                                  userObject.registrants.activeRegistrantsIndexes.find((elem) => elem === index)
                                    ? 'active-registrant'
                                    : ''
                                : multiModeSelectedResidentIndices.indexOf(index) !== -1
                                ? 'active-registrant'
                                : ''
                        }`}
                        key={index}
                        onClick={() => {
                            if (isMultiMode) {
                                const indexInMultiModeArray = multiModeSelectedResidentIndices.indexOf(index);
                                if (indexInMultiModeArray === -1) {
                                    setMultiModeSelectedResidentIndices((prevIndices) => [...prevIndices, index]);
                                } else {
                                    setMultiModeSelectedResidentIndices((prevIndices) =>
                                        prevIndices.filter((e) => e !== index),
                                    );
                                }
                            } else {
                                setUser({
                                    ...userObject,
                                    registrants: { ...userObject.registrants, activeRegistrantsIndexes: [index] },
                                });
                            }
                        }}
                    >
                        {registrant.Image ? (
                            <div
                                className="resident-image resident-image-bk"
                                style={{
                                    background: `url(${registrant.Image}) no-repeat`,
                                    backgroundPosition: 'center',
                                }}
                            />
                        ) : (
                            <div className="relative-avatar">
                                {registrant && !!registrant.FirstName && registrant.FirstName.charAt(0)}
                                {registrant && !!registrant.LastName && registrant.LastName.charAt(0)}
                            </div>
                        )}
                    </div>
                    <div data-tooltip={`${registrant.FirstName} ${registrant.LastName}`} data-position="top left">
                        <div className="registrant-name-header">
                            {registrant && `${registrant.FirstName} ${registrant.LastName}`}
                        </div>
                    </div>
                </div>
            );
        });
    };

    const AllResidentLink = ({ userObject, isManagerRequired }) => {
        return (
            <div key="all" className="name-image-container">
                <div
                    className={`resident-image-container
						${userObject.registrants.activeRegistrantsIndexes.find((elem) => elem === 'all-users') ? 'active-registrant' : ''}`}
                    key={'all-users'}
                    onClick={() => {
                        if (!isMultiMode) {
                            setUser({
                                ...userObject,
                                registrants: { ...userObject.registrants, activeRegistrantsIndexes: ['all-users'] },
                            });
                        }
                    }}
                >
                    <div className="relative-avatar">
                        <Icon disabled={isMultiMode} name="users" />
                    </div>
                    {isMultiMode && <div className="image-dimmer"></div>}
                </div>
                <div data-tooltip={'All Users'} data-position="top left">
                    <div className="registrant-name-header">All Users</div>
                </div>
            </div>
        );
    };
    return (
        <header className="header">
            <div className="resident-links">
                {isMultiSelectEnabled &&
                    (isMultiMode ? (
                        <div key="multi-ok" className="name-image-container">
                            <Button
                                className="resident-image-container"
                                icon="check"
                                onClick={handleMultiSelectButtonClick}
                                positive
                                compact
                            ></Button>
                            <div data-tooltip={'Okay'} data-position="top left">
                                <div className="registrant-name-header">OK</div>
                            </div>
                        </div>
                    ) : (
                        <div key="multi" className="name-image-container">
                            <Button
                                className="resident-image-container"
                                icon="add user"
                                onClick={handleMultiSelectButtonClick}
                                primary
                                compact
                            ></Button>
                            <div data-tooltip={'Select multiple users'} data-position="top left">
                                <div className="registrant-name-header">Multi</div>
                            </div>
                        </div>
                    ))}
                {isSendMessagesPage && (
                    <AllResidentLink userObject={userObject} isManagerRequired={isManagerRequired} />
                )}

                <ConnectedRegistrantsList
                    userObject={userObject}
                    isManagerRequired={isManagerRequired}
                ></ConnectedRegistrantsList>
            </div>

            <div className="menuBar">
                <ProfileHeader openSideBar={() => setShowSidebar(true)} userObject={userObject} pageName={pageName} />
                <Sidebar
                    as={Menu}
                    animation="push"
                    icon="labeled"
                    inverted
                    onHide={() => !showSidebarProp && setShowSidebar(false)}
                    vertical
                    visible={showSidebar}
                    width="thin"
                >
                    {menus.map((menu) => {
                        if (menu.showToCommunityManagerOnly) {
                            const activeRegistrant =
                                userObject.registrants.list[userObject.registrants.activeRegistrantsIndexes[0]];
                            return activeRegistrant && activeRegistrant.isCommunityManager ? (
                                <Menu.Item
                                    as="a"
                                    onClick={() => navigate(menu.location)}
                                    key={menu.location}
                                    className={menu.pageName + 'button'}
                                >
                                    {menu.icon}
                                    {menu.pageName}
                                </Menu.Item>
                            ) : null;
                        }
                        return (
                            <Menu.Item
                                as="a"
                                onClick={() => navigate(menu.location)}
                                key={menu.location}
                                className={menu.pageName + 'button'}
                            >
                                {menu.icon}
                                {menu.pageName}
                            </Menu.Item>
                        );
                    })}
                </Sidebar>
            </div>
        </header>
    );
};

export default Header;
